import { useBooleanState } from '@gettactic/helpers';
import { getApiErrorMessage } from '@gettactic/helpers/src/errors/getApiErrorMessage';
import React from 'react';
import { Button } from '../../../buttons';
import { Modal } from '../Modal';
import { ConfirmModalProps } from './ConfirmModal.types';
import { useBreakpoint } from '@gettactic/helpers/src/hooks/useBreakpoint';
import { MobileActionSheet } from '@v2/mobile/MobileActionSheet/MobileActionSheet';

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  message,
  onOpenChange,
  onConfirm,
  onCancel,
  onExtra,
  confirmProps,
  cancelProps,
  extraProps,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  extraLabel = '',
  footerProps,
  isOpen,
  children,
  ...rest
}) => {
  const isLoading = useBooleanState(false);
  const [asyncError, setAsyncError] = React.useState('');
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint('md');

  const handleConfirm = async () => {
    setAsyncError('');

    if (onConfirm) {
      isLoading.true();
      const results = await onConfirm();
      if (results) {
        const error = getApiErrorMessage(results);
        setAsyncError(error);
      }
      isLoading.false();
    }
  };

  const handleClose = () => {
    if (isLoading.state) return;
    onOpenChange?.(false);
  };

  const handleExtra = async () => {
    setAsyncError('');

    if (onExtra) {
      isLoading.true();
      const results = await onExtra();
      if (results) {
        const error = getApiErrorMessage(results);
        setAsyncError(error);
      }
      isLoading.false();
    }
  };

  const handleCancel = () => {
    if (isLoading.state) return;

    if (onCancel) {
      onCancel();
    }

    handleClose();
  };

  if (!isMd) {
    return (
      <MobileActionSheet
        isOpen={isOpen}
        title={rest.title || undefined}
        message={message || undefined}
        onOpenChange={(isOpen) => !isOpen && handleClose()}
        actions={[
          {
            onSelect: handleConfirm,
            label: confirmLabel,
            buttonProps: { variant: 'default' }
          },
          {
            onSelect: handleCancel,
            label: cancelLabel
          },
          onExtra && extraLabel
            ? {
                onSelect: onExtra,
                label: extraLabel
              }
            : undefined
        ]}
      />
    );
  }

  return (
    <Modal
      opened={isOpen}
      {...rest}
      closeOnOverlayClick={isLoading.state ? false : rest.closeOnOverlayClick}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
      onClose={() => null}
    >
      {message}
      {children}
      <Modal.Footer {...footerProps}>
        <div className={'flex w-full justify-between gap-2'}>
          {onExtra && extraLabel && (
            <Button
              onClick={handleExtra}
              className="w-full sm:ml-3 sm:w-auto [.tactic-v2_&]:text-primary"
              layout="squared"
              variant="blue"
              type="submit"
              loading={isLoading.state}
              {...(extraProps as object)}
            >
              {extraLabel}
            </Button>
          )}
          <div>
            <Button
              onClick={handleCancel}
              className="w-full sm:w-auto"
              layout="squared"
              variant="white"
              font="boldGray"
              disabled={isLoading.state}
              {...(cancelProps as object)}
            >
              {cancelLabel}
            </Button>
            <Button
              onClick={handleConfirm}
              className="w-full sm:ml-3 sm:w-auto"
              layout="squared"
              variant="primary"
              type="submit"
              loading={isLoading.state}
              {...(confirmProps as object)}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
